(ns ui.sticker-dialogs.recent
  "The recent hashtags, mentions and locations that the sticker dialogs
   offer as chips. They live in the `storrito:editor2:recent`
   localStorage entry (`ui.editor2.recent`), in the camelCase JSON shape
   that the former Reagent dialogs wrote there, so the lists of existing
   users stay readable. The pure part: cache entries and list updates;
   `now` is passed in for testability."
  (:require [clojure.string :as str]
            [storrito.instagram.story.post.util :as post-util]
            [ui.sticker-dialogs.model :as model]))

(def max-hashtags
  50)

(def max-mentions
  100)

(def max-locations
  25)

(defn cached-at
  "When the `entry` was remembered. An entry without a timestamp counts
   as the oldest. `sort-by` is stable, so several of them keep their
   stored order."
  [entry]
  (or (:cachedAt entry)
      (:cached-at entry)
      0))

(defn put-latest
  "Puts `entry` at the front of `entries`, replacing the entry with the
   same key, and keeps at most `max-entries`."
  [entries entry key-f max-entries]
  (->> entries
       (remove #(= (key-f entry)
                   (key-f %)))
       (cons entry)
       (take max-entries)
       (vec)))

(defn hashtag-key
  [entry]
  (or (:hashtagKey entry)
      (:hashtag-key entry)
      (model/normalize-hashtag (:hashtag entry))))

(defn hashtag-entry
  [hashtag now]
  (let [key (model/normalize-hashtag hashtag)]
    (when (and (seq key)
               (post-util/valid-hashtag? key))
      {:hashtag key
       :hashtagKey key
       :cachedAt now})))

(defn valid-hashtag-entry?
  [entry]
  (boolean
    (and (:hashtag entry)
         (post-util/valid-hashtag? (:hashtag entry)))))

(defn sorted-hashtags
  [entries]
  (->> entries
       (filter valid-hashtag-entry?)
       (sort-by cached-at >)
       (take max-hashtags)
       (vec)))

(defn remember-hashtags
  [entries hashtags now]
  (reduce (fn [entries entry]
            (put-latest entries entry hashtag-key max-hashtags))
          (vec entries)
          (keep #(hashtag-entry % now) hashtags)))

(defn mention-key
  [entry]
  (or (:usernameKey entry)
      (:username-key entry)
      (model/normalize-username (:username entry))))

(defn mention-user-id
  [entry]
  (or (:userId entry)
      (:user-id entry)))

(defn mention-result
  "The `{:username :user-id}` of a cache entry, the shape the mention
   sticker needs."
  [entry]
  {:username (:username entry)
   :user-id (mention-user-id entry)})

(defn mention-entry
  [{:keys [username user-id]} now]
  (let [key (model/normalize-username username)]
    (when (and username
               user-id
               (not (str/blank? key)))
      {:username username
       :usernameKey key
       :userId user-id
       :cachedAt now})))

(defn same-mention?
  [entry other]
  (or (= (mention-user-id entry) (mention-user-id other))
      (= (mention-key entry) (mention-key other))))

(defn remember-mention
  [entries result now]
  (if-let [entry (mention-entry result now)]
    (->> entries
         (remove #(same-mention? % entry))
         (cons entry)
         (take max-mentions)
         (vec))
    (vec entries)))

(defn remove-mention
  [entries entry]
  (vec (remove #(same-mention? % entry) entries)))

(defn sorted-mentions
  [entries]
  (->> entries
       (filter (fn [entry]
                 (and (:username entry)
                      (mention-user-id entry))))
       (sort-by cached-at >)
       (take max-mentions)
       (vec)))

(defn find-mention
  "The cached validation result for `username`, if any."
  [entries username]
  (let [key (model/normalize-username username)]
    (some (fn [entry]
            (when (= key (mention-key entry))
              (mention-result entry)))
          (sorted-mentions entries))))

(defn location-key
  [location]
  (or (:locationKey location)
      (:location-key location)
      (:external_id location)
      (:external-id location)
      (str/join "|" [(:name location)
                     (:address location)
                     (:lat location)
                     (:lng location)])))

(defn valid-location-entry?
  [location]
  (boolean
    (and (map? location)
         (seq (:name location))
         (seq (location-key location)))))

(defn location-entry
  [location now]
  (when (valid-location-entry? location)
    (assoc location
           :locationKey (location-key location)
           :cachedAt now)))

(defn remember-location
  [entries location now]
  (if-let [entry (location-entry location now)]
    (put-latest entries entry location-key max-locations)
    (vec entries)))

(defn remove-by-key
  [entries key-f key]
  (vec (remove #(= key (key-f %)) entries)))

(defn sorted-locations
  [entries]
  (->> entries
       (filter valid-location-entry?)
       (sort-by cached-at >)
       (take max-locations)
       (vec)))
